import { Explicit } from './Explicit';

/**
 * The declaration an artist makes about their own work.
 *
 * ## A question, not a warning
 *
 * Worded as "does this have explicit content" rather than anything that sounds
 * like a policy being enforced. Nothing here is checked and nothing is
 * moderated by it - it is the artist telling us, the same way every distributor
 * asks, and it is used for exactly two things: the marker beside the title, and
 * whether rooms that have opted out will take it.
 *
 * ## Off by default and it stays off if ignored
 *
 * A question nobody answers has to mean no, because the alternative is
 * asserting something about somebody's work that they never said. That puts the
 * responsibility where it belongs and makes the tick a deliberate act.
 */
export function ExplicitToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        data-explicit-toggle
        onClick={() => onChange(!value)}
        className={`flex items-center gap-3 rounded-[var(--radius-control)] border p-3.5 text-left transition-colors ${
          value ? 'border-line-strong bg-raised' : 'border-line hover:border-line-strong'
        }`}
      >
        <span
          aria-hidden
          className={`flex h-5 w-9 shrink-0 items-center rounded-[var(--radius-pill)] border transition-colors ${
            value ? 'border-transparent bg-accent' : 'border-line bg-app'
          }`}
        >
          <span
            className={`block h-3.5 w-3.5 rounded-full bg-ink transition-transform ${
              value ? 'translate-x-[1.15rem]' : 'translate-x-[3px]'
            }`}
          />
        </span>

        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
            {value && <Explicit />}
            Explicit content
          </span>
          <span className="text-xs leading-relaxed text-soft">
            Swearing or adult themes. Shows an{' '}
            <span className="font-mono text-[11px]">E</span> beside the title, and hosts who have
            turned explicit tracks off will not take it.
          </span>
        </span>
      </button>
    </div>
  );
}
